import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { interviewStateSchema, type InterviewState } from '@proctoring/shared';
import { useInterviews } from '../api/queries.js';
import { FullPageMessage } from '../components/FullPageMessage.js';
import { INTERVIEW_STATE_LABELS, StateBadge } from '../components/StateBadge.js';
import { formatDateTime } from '../components/format.js';

const STATES = interviewStateSchema.options;

export function InterviewsPage(): ReactElement {
  // `undefined` means "all"; the API omits the query param entirely in that case.
  const [state, setState] = useState<InterviewState | undefined>(undefined);
  const { data, isPending, error } = useInterviews(state);

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-session-text">Interviews</h1>
          <p className="mt-1 text-sm text-session-muted">
            Every scheduled interview across the organisation.
          </p>
        </div>
        <Link to="/interviews/new" className="btn-primary">
          New interview
        </Link>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by state">
        <button
          type="button"
          onClick={() => setState(undefined)}
          aria-pressed={state === undefined}
          className={state === undefined ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
        >
          All
        </button>
        {STATES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => setState(candidate)}
            aria-pressed={state === candidate}
            className={state === candidate ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
          >
            {INTERVIEW_STATE_LABELS[candidate]}
          </button>
        ))}
      </div>

      {isPending ? (
        <p className="panel mt-6 px-5 py-6 text-sm text-session-muted">Loading interviews…</p>
      ) : error ? (
        <FullPageMessage title="Could not load interviews" detail={messageForError(error)} />
      ) : data.length === 0 ? (
        <p className="panel mt-6 px-5 py-6 text-sm text-session-muted">
          No interviews match this filter.
        </p>
      ) : (
        <ul className="panel mt-6 overflow-hidden">
          {data.map((interview) => (
            <li
              key={interview.id}
              data-testid="interview-row"
              className="border-b border-session-border last:border-b-0"
            >
              <Link
                to={`/interviews/${interview.id}`}
                className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-session-panel-raised"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-session-text">
                    {interview.jobProfile.name}
                  </p>
                  <p className="mt-0.5 text-sm text-session-muted">
                    {formatDateTime(interview.scheduledAt)}
                  </p>
                </div>
                <StateBadge state={interview.state} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
